/**
 * Notification Forms — staff-facing catalog of the 8 mandated intake/notification
 * forms every fee earner and support staff member must be able to raise:
 *   FORM-01 New Matter AML
 *   FORM-02 Conflicts Check
 *   FORM-03 File Review
 *   FORM-04 Complaints Intake
 *   FORM-05 Data Breach Notification
 *   FORM-06 Gifts Declaration
 *   FORM-07 Training Attendance
 *   FORM-08 Economic Crime Incident
 *
 * Read-only catalog surface. Backend endpoint (/api/policy/forms) was
 * ungated in the Team Policy Ack PR so this page is visible to all staff.
 * Registers stay on the separate compliance-only RegistersForms.jsx page
 * because they hold cross-staff PII.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, AlertCircle, RefreshCw } from "lucide-react";
import { authFetch } from "../../config/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

function TierBadge({ tier }) {
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs ${
        tier === "red" ? "bg-red-100 text-red-900" : "bg-yellow-100 text-yellow-900"
      }`}
    >
      {tier}
    </span>
  );
}

// BM-H3509: forms that are live CaseMinder pages open the page itself, not
// the catalogue entry. A complaint raised here used to go nowhere: the card
// led to a description, while Report a Complaint is where complaints land.
// BM-H3819 (6 Oct 2026): every form leads somewhere real. Until then only
// FORM-04 did; the rest opened a page reading "Field definition not yet defined".
export const LIVE_FORM_ROUTES = {
  "FORM-01": "/new-matter",                    // the new matter AML steps run in the matter pipeline
  "FORM-02": "/conflict-search",
  "FORM-03": "/compliance/file-reviews",       // the File Review Register, where a review is started (BM-H632)
  "FORM-04": "/complaints/new",
  "FORM-05": "/compliance/registers/REG-04",   // GDPR Breach Register: staff add the breach there
  "FORM-06": "/compliance/registers/REG-07",   // Gifts and Hospitality Register
  "FORM-07": "/compliance/registers/REG-08",   // Training and CPD Register
  "FORM-08": "/mlro/report-suspicion",         // goes to the MLRO only
};
export const FORM_NOTES = {
  "FORM-01": "Completed as part of opening a new matter (conflict check, onboarding, AML and the FORF).",
  "FORM-02": "Run a conflict search; the result is recorded on the matter.",
  "FORM-03": "Start a file review from the File Review Register.",
  "FORM-05": "Add the breach to the GDPR Breach Register.",
  "FORM-06": "Add the gift or hospitality to the Gifts and Hospitality Register.",
  "FORM-07": "Add the training to the Training and CPD Register.",
  "FORM-08": "Report any suspicion of money laundering, sanctions breach, fraud or tax evasion to the MLRO.",
};

function FormCard({ item }) {
  const ref = item.ref || item.form_ref;
  const gp = item.governing_policy || {};
  const note = FORM_NOTES[ref];
  return (
    <Link
      to={LIVE_FORM_ROUTES[ref] || `/policy/forms/${encodeURIComponent(ref)}`}
      data-testid={`form-card-${ref}`}
      className="block border rounded-lg p-4 hover:bg-slate-50 hover:shadow-sm transition"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-xs text-slate-500">{ref}</span>
        <TierBadge tier={item.tier} />
      </div>
      <div className="font-medium text-slate-900">{item.name}</div>
      {note && <div className="mt-1 text-xs text-slate-600">{note}</div>}
      <div className="mt-2 text-xs text-slate-500">
        Governing policy: {gp.name || item.governing_policy_slug}
      </div>
    </Link>
  );
}

export default function NotificationForms() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await authFetch(`${API}/api/policy/forms`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setForms(j.forms || []);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="notification-forms-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading notification forms...
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="notification-forms-error" className="p-6 text-red-700">
        <AlertCircle className="inline-block mr-2" size={16} />
        Failed to load notification forms: {error}
      </div>
    );
  }

  return (
    <div data-testid="notification-forms-page" className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <FileText size={22} />
          <h1 className="text-2xl font-semibold text-slate-900">Notification Forms</h1>
          <span data-testid="forms-count" className="text-sm text-slate-500">
            ({forms.length})
          </span>
        </div>
        <p className="mt-2 text-sm text-slate-600 max-w-3xl">
          Use these forms to raise compliance notifications — client complaints,
          data breaches, gifts and hospitality declarations, conflicts, and
          economic-crime incidents. Filed forms route to the relevant compliance
          officer (COLP, MLRO, MLCO, or DPO) for action.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {forms.map((f) => (
          <FormCard key={f.ref || f.form_ref} item={f} />
        ))}
      </div>
    </div>
  );
}
